import type { AnchorVisibleWindow } from '@edge/charts'

/**
 * Visible-window presets + time helpers for the History Chart Grid gear.
 * Windows are minutes from ET midnight (240 = 4:00 AM, 1200 = 8:00 PM). The
 * backend only serves the 4:00–20:00 session, so every window clamps to it.
 */

/** Earliest / latest selectable minute — the served extended-hours session. */
export const SESSION_MIN = 240 // 4:00 AM ET
export const SESSION_MAX = 1200 // 8:00 PM ET

// ─── Timeframe budget (Phase 2) ───
//
// How far back a multi-day window may reach depends on the chart's timeframe: a
// fixed bar budget divided by bars-per-session. This caps the Day picker AND
// sizes the preload, so the two never disagree. 2000 bars lets 1m span 2
// sessions (D[-1]) and 1h span ~125 (a month+ easily).

/** Max bars a visible window may span — the selection cap and the fetch cap. */
export const MAX_WINDOW_BARS = 2000

/** Minutes in the served extended session (4:00–20:00 ET). */
const SESSION_MINUTES = SESSION_MAX - SESSION_MIN // 960

/** Bars one session holds at a timeframe (daily+ = 1 bar per session). */
export function barsPerSession(periodMs: number): number {
  const periodMin = periodMs / 60_000
  if (periodMin >= 1440) return 1 // daily or coarser
  return Math.ceil(SESSION_MINUTES / periodMin)
}

/** Max sessions a window may span at a timeframe (≥ 1). */
export function maxSpanSessions(periodMs: number): number {
  return Math.max(1, Math.floor(MAX_WINDOW_BARS / barsPerSession(periodMs)))
}

/** Max Start days-back (End pinned at D[1]): span − 1, ≥ 0. */
export function maxDaysBack(periodMs: number): number {
  return maxSpanSessions(periodMs) - 1
}

/**
 * Furthest forward (outcome) session the End bound may reach: D[6] (`day5` in
 * the data model). Forward data is bounded by what has traded since the event,
 * and the records model itself only carries five forward days.
 */
export const MAX_FORWARD_SESSIONS = 5

/** Build a window, omitting offsets that are 0 (keeps single-day windows clean). */
function withOffsets(
  startMin: number,
  endMin: number,
  startDayOffset: number,
  endDayOffset: number,
): AnchorVisibleWindow {
  const w: AnchorVisibleWindow = { startMin, endMin }
  if (startDayOffset !== 0) w.startDayOffset = startDayOffset
  if (endDayOffset !== 0) w.endDayOffset = endDayOffset
  return w
}

/**
 * Clamp a window's day reach to what the timeframe allows. Runs at the grid
 * fan-out so the caps are structural — a persisted deep/forward window on a fine
 * timeframe can't reach a cell (and trigger an oversized fetch) ahead of the
 * gear's re-clamp effect. Caps the forward End at {@link MAX_FORWARD_SESSIONS},
 * then pulls the Start up so the total span fits the bar budget. Returns the
 * window unchanged when already within budget (or absent).
 */
export function clampWindowToBudget(
  window: AnchorVisibleWindow | undefined,
  periodMs: number,
): AnchorVisibleWindow | undefined {
  if (!window) return window
  const startOff = window.startDayOffset ?? 0
  const endOff = window.endDayOffset ?? 0
  const cappedEnd = Math.min(endOff, MAX_FORWARD_SESSIONS)
  const minStart = cappedEnd - (maxSpanSessions(periodMs) - 1)
  const cappedStart = Math.max(startOff, minStart)
  if (cappedStart === startOff && cappedEnd === endOff) return window
  return withOffsets(window.startMin, window.endMin, cappedStart, cappedEnd)
}

export interface WindowPreset {
  id: string
  label: string
  window: AnchorVisibleWindow
}

/** Ordered preset list shown in the gear. "Full session" is the default. */
export const WINDOW_PRESETS: WindowPreset[] = [
  { id: 'full', label: 'Full session', window: { startMin: 240, endMin: 1200 } },
  { id: 'regular', label: 'Regular hours', window: { startMin: 570, endMin: 960 } },
  { id: 'premarket', label: 'Pre-market', window: { startMin: 240, endMin: 570 } },
  { id: 'morning', label: 'Morning', window: { startMin: 570, endMin: 660 } },
  { id: 'afternoon', label: 'Afternoon', window: { startMin: 720, endMin: 960 } },
]

/** Clamp a minute value into the served session and to whole minutes. */
export function clampToSession(min: number): number {
  return Math.max(SESSION_MIN, Math.min(SESSION_MAX, Math.round(min)))
}

/** minutes-from-midnight → "HH:MM" (24h). */
export function minToTimeStr(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** "HH:MM" → minutes-from-midnight; null for an unparseable value. */
export function timeStrToMin(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  if (h > 23 || m > 59) return null
  return h * 60 + m
}

/** minutes-from-midnight → "9:30 AM" style label, for read-only display. */
export function minToLabel(min: number): string {
  const h24 = Math.floor(min / 60)
  const m = min % 60
  const period = h24 < 12 ? 'AM' : 'PM'
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}

/** The effective window for a panel state value (absent ⇒ full session). This
 *  matches charts-core's DEFAULT_ANCHOR_WINDOW; the `full` preset is the shared
 *  local source so this module needs no runtime charts-core import. */
export function resolveWindow(window: AnchorVisibleWindow | undefined): AnchorVisibleWindow {
  return window ?? WINDOW_PRESETS[0].window
}

/** Id of the preset matching a window, or 'custom' when none does. */
export function matchPresetId(window: AnchorVisibleWindow): string {
  // A multi-day window (any nonzero day offset) is always Custom, even when its
  // minutes coincide with a preset — e.g. D[-1] Open → D[1] Close shares Regular
  // hours' 570/960 but must NOT read as Regular (that would hide the Day pickers
  // and orphan the offsets). Presets are single-day by construction.
  if ((window.startDayOffset ?? 0) !== 0 || (window.endDayOffset ?? 0) !== 0) return 'custom'
  const hit = WINDOW_PRESETS.find(
    (p) => p.window.startMin === window.startMin && p.window.endMin === window.endMin,
  )
  return hit?.id ?? 'custom'
}

// ─── Session anchors (Custom picker) ───
//
// Phase 1 of the session-anchor model (see
// docs/superpowers/specs/2026-08-12-history-chart-window-anchors-design.md):
// Custom is built from named session points on the event day (D[1]) rather than
// raw clock times. Each point is a fixed ET wall-clock minute, so an anchor pair
// still resolves to the existing `{ startMin, endMin }` — no schema/charts-core
// change. Phase 2 adds the D[-N] day dimension.

export type SessionPointId = 'pm_open' | 'open' | 'midday' | 'close' | 'ah_close'

export interface SessionPoint {
  id: SessionPointId
  /** Short label shown on the picker (Day prefix is added by the control). */
  label: string
  /** Event-day ET minute this point resolves to. */
  min: number
}

/** The session points, in chronological order. */
export const WINDOW_ANCHOR_POINTS: SessionPoint[] = [
  { id: 'pm_open', label: 'PM Open', min: 240 }, // 4:00 AM
  { id: 'open', label: 'Open', min: 570 }, // 9:30 AM
  { id: 'midday', label: 'Midday', min: 720 }, // 12:00 PM
  { id: 'close', label: 'Close', min: 960 }, // 4:00 PM
  { id: 'ah_close', label: 'AH Close', min: 1200 }, // 8:00 PM
]

/** The session point at a given minute, or undefined (a non-anchor minute). */
export function pointForMin(min: number): SessionPoint | undefined {
  return WINDOW_ANCHOR_POINTS.find((p) => p.min === min)
}

/**
 * Picker label for a bound: the session-point label when the minute is an
 * anchor, else the formatted clock time (a legacy/stored window may hold a
 * non-anchor minute — keep it readable and still editable to an anchor).
 */
export function anchorLabelForMin(min: number): string {
  return pointForMin(min)?.label ?? minToLabel(min)
}

// ─── Day dimension (Phase 2) ───
//
// Each Custom bound picks a session, expressed as a day offset: 0 = the event
// day, -1 = the prior trading session, … The UI labels reuse History's D[n]
// convention (D[1] = event day, D[-1] = prior — no D[0]). How far back the Start
// may reach is timeframe-bounded (see maxDaysBack), so the picker is an
// open-ended stepper, not a fixed option list.

/**
 * UI label for a day offset, in History's D[n] convention (matches
 * `history-column-defs` dayLabel): offset 0 = D[1] (event day), negative maps
 * straight through (−1 → D[-1]), positive shifts up one (1 → D[2], the day after
 * — there is no D[0]).
 */
export function dayLabelForOffset(offset: number): string {
  if (offset === 0) return 'D[1]'
  return offset < 0 ? `D[${offset}]` : `D[${offset + 1}]`
}

/**
 * Lexical rank of a (dayOffset, minute) bound for ordering: earlier session, or
 * same session + earlier minute, ranks lower. Keeps End strictly after Start
 * across the day dimension.
 */
export function boundRank(dayOffset: number, min: number): number {
  return dayOffset * 24 * 60 + min
}
